import { coerceToString } from "./mapAnalysisToBooking";

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

const LABELED_PHONE_RE =
  /(?:tel(?:ephone)?|phone|mobile|mob(?:ile)?|fax|m\.?)\s*[:\s#-]*\s*([+\d][+\d\s()./-]{6,28})/gi;

const LABELED_WECHAT_RE =
  /(?:wechat|weixin|微信|wx)\s*[:\s#-]*\s*([a-zA-Z0-9_-]{4,30})/gi;

const SELLER_START =
  /(?:^|\n)\s*(?:seller|exporter|shipper|supplier|verkoper|from)\b/i;
const BUYER_START =
  /(?:^|\n)\s*(?:buyer|importer|consignee|bill to|invoice to|ship to|koper)\b/i;

const normalizePhone = (raw: string): string | null => {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) return null;
  if (/^20[0-9]{6}$/.test(digits)) return null;
  if (/^\d{4}$/.test(digits)) return null;
  return trimmed.replace(/\s{2,}/g, " ");
};

const unique = (values: string[]): string[] => [...new Set(values)];

const getSellerSection = (text: string): string => {
  const startMatch = text.match(SELLER_START);
  const start = startMatch?.index ?? 0;
  const afterStart = text.slice(start);
  const buyerMatch = afterStart.slice(40).match(BUYER_START);
  const end = buyerMatch?.index != null ? start + 40 + buyerMatch.index : start + 2200;
  return text.slice(start, Math.min(end, text.length));
};

const findNearSellerName = (
  text: string,
  sellerName: string | null | undefined,
  values: string[]
): string | null => {
  const name = coerceToString(sellerName);
  if (!name) return null;
  const probe = name.slice(0, Math.min(name.length, 24)).toLowerCase();
  const idx = text.toLowerCase().indexOf(probe);
  if (idx < 0) return null;
  const window = text.slice(idx, idx + 900);
  return values.find((value) => window.includes(value)) ?? null;
};

const pickFromSection = (
  text: string,
  sellerName: string | null | undefined,
  values: string[]
): string | null => {
  if (values.length === 0) return null;
  if (values.length === 1) return values[0];

  const section = getSellerSection(text);
  const inSection = values.filter((value) => section.includes(value));
  if (inSection.length === 1) return inSection[0];
  if (inSection.length > 1) {
    return findNearSellerName(section, sellerName, inSection) ?? inSection[0];
  }

  return findNearSellerName(text, sellerName, values) ?? values[0];
};

const extractEmails = (text: string): string[] =>
  unique([...text.matchAll(EMAIL_RE)].map((match) => match[0].toLowerCase()));

const extractPhones = (text: string): string[] => {
  const labeled: string[] = [];
  for (const match of text.matchAll(LABELED_PHONE_RE)) {
    const normalized = normalizePhone(match[1] ?? "");
    if (normalized) labeled.push(normalized);
  }

  const generic = [...text.matchAll(/\+?\d[\d\s()./-]{7,20}\d/g)]
    .map((match) => normalizePhone(match[0]))
    .filter((value): value is string => Boolean(value));

  return unique([...labeled, ...generic]);
};

const extractWechats = (text: string): string[] => {
  const labeled = [...text.matchAll(LABELED_WECHAT_RE)]
    .map((match) => match[1]?.trim())
    .filter(Boolean) as string[];
  return unique(labeled);
};

export interface SupplierContactExtract {
  email: string | null;
  phone: string | null;
  wechat: string | null;
}

export const extractSupplierContact = (params: {
  invoiceText?: string | null;
  sellerName?: unknown;
  sellerEmail?: unknown;
  sellerPhone?: unknown;
  sellerWechat?: unknown;
}): SupplierContactExtract => {
  const aiEmail = coerceToString(params.sellerEmail);
  const aiPhone = coerceToString(params.sellerPhone);
  const aiWechat = coerceToString(params.sellerWechat);
  const text = params.invoiceText?.trim() ?? "";

  if (!text) {
    return { email: aiEmail, phone: aiPhone, wechat: aiWechat };
  }

  return {
    email: aiEmail ?? pickFromSection(text, params.sellerName, extractEmails(text)),
    phone: aiPhone ?? pickFromSection(text, params.sellerName, extractPhones(text)),
    wechat:
      aiWechat ?? pickFromSection(text, params.sellerName, extractWechats(text)),
  };
};
